import React from 'react';
import Markdown, { type Components } from 'react-markdown';
import classNames from 'classnames';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { MessageStatus } from '@/types/message.types';
import type { TextMessageProps } from './types';

const markdownComponents: Components = {
  ul: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <ul
      className="sf:list-disc sf:flex sf:flex-col sf:ml-4 sf:gap-1"
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <ol
      className="sf:list-decimal sf:flex sf:flex-col sf:ml-4 sf:gap-1"
      {...props}
    >
      {children}
    </ol>
  ),
  a: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <a
      className="sf:text-widget-button-color sf:hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  h1: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h1 className="sf:text-xl sf:font-bold" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h2 className="sf:text-lg sf:font-bold" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h3 className="sf:text-base sf:font-bold" {...props}>
      {children}
    </h3>
  ),
  h4: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h4 className="sf:text-base sf:font-semibold" {...props}>
      {children}
    </h4>
  ),
  h5: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h5 className="sf:text-sm sf:font-semibold" {...props}>
      {children}
    </h5>
  ),
  h6: ({ children, ...props }: React.PropsWithChildren<object>) => (
    <h6 className="sf:text-xs sf:font-semibold" {...props}>
      {children}
    </h6>
  ),
};

const remarkPlugins = [remarkGfm, remarkBreaks];

/**
 * TextMessage - Renders plain text and rich text messages
 */
export const TextMessage = React.memo<TextMessageProps>(({ message }) => {
  if (!message.messageContent?.trim().length) {
    return null;
  }

  return (
    <div
      className={classNames({
        'sf:message-bubble sf:flex sf:flex-col sf:px-2 sf:py-2 sf:rounded-xl sf:w-fit sf:shadow-message-bubble sf:text-sm sf:leading-5 sf:whitespace-pre-wrap sf:break-words sf:select-text sf:gap-2':
          true,
        'sf:max-w-[var(--w-message-bubble)]': message?.isSentFromSleekflow,
        'sf:max-w-[var(--w-message-bubble-full)]':
          !message?.isSentFromSleekflow,
        'sf:bg-white/60 sf:text-content-primary': message?.isSentFromSleekflow,
        'sf:bg-widget-message-background-color sf:text-widget-message-text-color':
          !message?.isSentFromSleekflow &&
          message?.status !== MessageStatus.FAILED,
        'sf:bg-red-10 sf:text-red-90':
          !message?.isSentFromSleekflow &&
          message?.status === MessageStatus.FAILED,
      })}
    >
      <Markdown remarkPlugins={remarkPlugins} components={markdownComponents}>
        {message?.messageContent}
      </Markdown>
    </div>
  );
});

TextMessage.displayName = 'TextMessage';
